import { Injectable } from '@angular/core';
import { CRMItemHistory, StandardCRMItemHistoryEventType, StructCapItemHistoryEventType } from '../../shared/models/crm/CRMItemHistory.model';
import { CRMStatus, CRMStatusLabels } from '../../shared/models/crm/CRMStatus';
import { CRMCompanyStorm, CRMCompanyStormLabels } from '../../shared/models/crm/CRMCompanyStorm';
import { CourtshipTypeEnum, CourtshipTypeLabels } from '../../shared/enums/courtship-type.enum';

@Injectable({
  providedIn: 'root'
})
export class CrmHistoryUtilsService {

  public readonly statusLabels = CRMStatusLabels;
  public readonly courtshipLabels = CourtshipTypeLabels;
  public readonly stormLabels = CRMCompanyStormLabels;

  private timeStampHistoryEventTypes = [
    StandardCRMItemHistoryEventType.STATUS,
    StandardCRMItemHistoryEventType.STORM,
    StandardCRMItemHistoryEventType.ETAKI,
    StandardCRMItemHistoryEventType.SILENCED_UNTIL,
    StandardCRMItemHistoryEventType.COURTSHIP,
    StandardCRMItemHistoryEventType.AGENDA,
    StructCapItemHistoryEventType.STATUS,
    StructCapItemHistoryEventType.STORM,
    StructCapItemHistoryEventType.ETAKI,
    StructCapItemHistoryEventType.SILENCED_UNTIL,
    StructCapItemHistoryEventType.COURTSHIP,
    StructCapItemHistoryEventType.AGENDA,
  ];

  private singleStateEvents = [
    StandardCRMItemHistoryEventType.AGENDA,
    StructCapItemHistoryEventType.AGENDA,
  ];

  constructor() { }

  public getCrmItemHistoryComparison(history: Array<CRMItemHistory>): Array<HistoryComparison> {
    const historyComparisonArray = new Array<HistoryComparison>();
    history = history.sort((a, b) => a.eventTimestamp < b.eventTimestamp ? -1 : 1);
    type CRMItemHistoryEvent = StandardCRMItemHistoryEventType | StructCapItemHistoryEventType;
    const historyMap: Map<CRMItemHistoryEvent, CRMItemHistory> = new Map<CRMItemHistoryEvent, CRMItemHistory>();
    let participants: Array<string> = new Array<string>();
    history.forEach(value => {
      if (value.eventType === StandardCRMItemHistoryEventType.PARTICIPANT) {
        if (value.eventAction && value.eventAction.toLowerCase() === 'deleted') {
          participants = participants.filter(participant => participant !== value.note);
        }
        if (value.eventAction && value.eventAction.toLowerCase() === 'added') {
          participants.push(value.note);
        }
        value.note = participants.join(', ');
      }


      if (this.singleStateEvents.includes(value.eventType)) {
        const historyComparison: HistoryComparison = {itemBefore: null, itemAfter: value};
        historyComparisonArray.push(historyComparison);
        return;
      }

      if ((value.eventAction && value.eventAction.toLowerCase() !== 'deleted') ||
        (value.eventType === StandardCRMItemHistoryEventType.PARTICIPANT)) {
        // In case of a courtship affirmation event, replace the timestamp with the affirmed status
        if (value.eventType === StandardCRMItemHistoryEventType.COURTSHIP_LAST_AFFIRMED &&
          historyMap.has(StandardCRMItemHistoryEventType.COURTSHIP)
        ) {
          value.note = historyMap.get(StandardCRMItemHistoryEventType.COURTSHIP).note;
        }
        const historyComparison: HistoryComparison = {itemBefore: null, itemAfter: value};
        if (historyMap.has(value.eventType)) {
          historyComparison.itemBefore = historyMap.get(value.eventType);
        }
        historyMap.set(value.eventType, value);
        historyComparisonArray.push(historyComparison);
      }
    });
    return historyComparisonArray;
  }

  public getCrmItemHistoryDescription(historyItem: CRMItemHistory): string {
    if (historyItem === null || historyItem === undefined || historyItem.note === '') {
      return '-';
    }
    if (historyItem.eventType === StandardCRMItemHistoryEventType.STATUS) {
      const status: CRMStatus = CRMStatus[historyItem.note];
      if (status) {
        return this.statusLabels[status];
      }
    }
    if (historyItem.eventType === StandardCRMItemHistoryEventType.STORM ||
      historyItem.eventType === StructCapItemHistoryEventType.STORM
    ) {
      const storm: CRMCompanyStorm = CRMCompanyStorm[historyItem.note];
      if (storm) {
        return this.stormLabels[storm];
      }
    }
    if (historyItem.eventType === StandardCRMItemHistoryEventType.COURTSHIP ||
       historyItem.eventType === StandardCRMItemHistoryEventType.COURTSHIP_LAST_AFFIRMED ||
       historyItem.eventType === StructCapItemHistoryEventType.COURTSHIP ||
       historyItem.eventType === StructCapItemHistoryEventType.COURTSHIP_LAST_AFFIRMED) {
      const courtship: CourtshipTypeEnum = CourtshipTypeEnum[historyItem.note];
      if (courtship) {
        return this.courtshipLabels[courtship];
      }
    }
    if (historyItem.eventType === StandardCRMItemHistoryEventType.SILENCED_UNTIL ||
      historyItem.eventType === StructCapItemHistoryEventType.SILENCED_UNTIL
    ) {
      return this.formatSilencedUntil(historyItem.note);
    }
    return historyItem.note;
  }

  public isTimestampEventType(event: StandardCRMItemHistoryEventType|StructCapItemHistoryEventType) {
    return this.timeStampHistoryEventTypes.includes(event);
  }

  private formatSilencedUntil(silencedUntil: string) {
    if (silencedUntil === '-') {
      return '-';
    }
    return new Date(silencedUntil).toLocaleDateString();
  }

}

export interface HistoryComparison {
  itemBefore: CRMItemHistory;
  itemAfter: CRMItemHistory;
}
